import WorkFlowView from './WorkFlowView';
import ViewState from '../support/ViewState';
import RuleViewConstructorArgs from '../constructor/RuleViewConstructorArgs';
import * as d3 from 'd3';
import { CaseReason, NodeType, ResolutionCaseType, RuleJsonModel } from '../../../../domain/collection/rule/FlowDomains';
import { enrichSyntax, Syntax } from '@trlogic/ui-pack';
import View from './View';
import EventNodeView from '../node/EventNodeView';
import NodeMouseEvent from '../support/NodeMouseEvent';
import ActionNodeView from '../node/ActionNodeView';
import ConditionNodeView from '../node/ConditionNodeView';
import StartNodeView from '../node/StartNodeView';
import AbstractNodeView from '../node/AbstractNodeView';
import TransitionView from '../node/TransitionView';
import { generateUUID } from '../../../../util/Generator';
import {
  ActionToken,
  ConditionToken,
  EventToken,
  FraudToken,
  NotFraudToken,
  SuspiciousToken,
  EscalatedToken,
  WorkflowToken,
} from '../../../../domain/collection/rule/RuleDomains';
import ViewActions from '../constants/ViewActions';
import NotFraudNodeView from '../node/NotFraudNodeView';
import EscalatedNodeView from '../node/EscalatedNodeView';
import { TransactionJsonModel } from '../../../../domain/stream/transaction/TransactionDomains';
import { ActionJsonModel } from '../../../../domain/collection/rule/action/ActionDomains';
import JQuery from 'jquery';
import PriorityNodeView from '../node/PriorityNodeView';
import MouseDownEvent = JQuery.MouseDownEvent;
import MouseUpEvent = JQuery.MouseUpEvent;
import ClickEvent = JQuery.ClickEvent;
import MouseMoveEvent = JQuery.MouseMoveEvent;
import WorkflowNodeUtil from '../../../../util/WorkflowNodeUtil';

declare type JQueryEvent = MouseDownEvent | MouseUpEvent | ClickEvent | MouseMoveEvent;

let margin = { top: 50, right: 120, bottom: 20, left: 220 },
  width = 960 - margin.right - margin.left,
  height = 800 - margin.top - margin.bottom;
let node: any = undefined;

abstract class AbstractWorkFlowView implements WorkFlowView {
  protected model: RuleJsonModel;
  protected eventItems: TransactionJsonModel[];
  protected actionItems: ActionJsonModel[];
  protected parent: string;
  protected viewState: ViewState;
  protected startView: View<Syntax> | undefined;
  protected eventViews: Array<View<Syntax>>;
  protected priorityNodeView: View<Syntax> | undefined;
  protected conditionViews: Array<View<Syntax>>;
  protected actionViews: Array<View<Syntax>>;
  protected resolutionViews: Array<View<Syntax>>;
  protected transitionViews: Array<View<Syntax>>;
  protected el: string;
  protected containerNode: d3.Selection<any, any, any, any>;
  protected treeNode: any;
  private height: number;
  private width: number;
  private transformX: number = -60;
  private transformY: number = -30;
  private currentScale: number = 1;
  protected currentShadow: string | undefined;
  private _onChangeFunction: ((rule: RuleJsonModel) => void) | undefined;
  protected menuOpen: boolean = false;

  protected constructor(args: RuleViewConstructorArgs) {
    this.model = args.model as RuleJsonModel;
    this.eventItems = [];
    this.actionItems = [];
    this.parent = args.parent;
    this.viewState = { action: ViewActions.FREE };
    this.eventViews = new Array<View<Syntax<EventToken>>>();
    this.conditionViews = new Array<View<Syntax<ConditionToken>>>();
    this.actionViews = new Array<View<Syntax<ActionToken>>>();
    this.resolutionViews = new Array<View<Syntax<FraudToken | NotFraudToken | SuspiciousToken | EscalatedToken>>>();
    this.transitionViews = new Array<View<Syntax>>();
    this.el = this.parent;
    this.containerNode = d3.select(this.parent);
    this.treeNode = d3.hierarchy(this.model.workflow, function (d: any) {
      return d.children;
    });
    this.height = args.height;
    this.width = args.width;
    enrichSyntax((args.model as RuleJsonModel).workflow, undefined);
  }

  public getParent(): string {
    return this.parent;
  }

  public getNode(): string {
    return this.el;
  }

  public getModel<T>(): T {
    return this.model as T;
  }

  public getWorkflow(): Syntax {
    return this.model.workflow;
  }

  public getTransitionViews(): Array<View<Syntax>> {
    return this.transitionViews;
  }

  public getConditionViews(): Array<View<Syntax<ConditionToken>>> {
    return this.conditionViews as Array<View<Syntax<ConditionToken>>>;
  }

  public getActionViews(): Array<View<Syntax<ActionToken>>> {
    return this.actionViews as Array<View<Syntax<ActionToken>>>;
  }

  public getResolutionViews(): Array<View<Syntax<FraudToken | SuspiciousToken | NotFraudToken>>> {
    return this.resolutionViews as Array<View<Syntax<FraudToken | SuspiciousToken | NotFraudToken>>>;
  }

  public setState(viewState: ViewState): void {
    this.viewState = viewState;
  }

  public initialize(): void {
    this.viewState = { action: ViewActions.FREE };
    this.render();
  }

  public render(): void {
    const oldNode = d3.select('#workflow-container');
    oldNode.remove();
    const workflowContainer = (d3.select('#workflow').node() as any).getBoundingClientRect();

    node = this.containerNode
      .append('svg')
      .attr('id', 'workflow-container')
      .attr('width', window.innerWidth / 3 - 160)
      .attr('height', workflowContainer.height)
      .style('position', 'absolute')
      .append('g')
      .attr('id', 'workflow-container-group')
      .attr('transform', 'translate(' + this.transformX + ',' + this.transformY + ') scale( ' + this.currentScale + ')');
    this.containerNode = node;
    this.createTree();
    this.initializeListeners();
  }

  public onChange(func: (rule: RuleJsonModel) => void) {
    this._onChangeFunction = func;
  }

  public terminate() {
    this.eventViews = this.conditionViews = this.actionViews = this.transitionViews = this.resolutionViews = [];
    this.priorityNodeView = undefined;
    this.eventViews.forEach((e) => e.terminate());
    this.conditionViews.forEach((e) => e.terminate());
    this.actionViews.forEach((e) => e.terminate());
    this.resolutionViews.forEach((e) => e.terminate());
    this.transitionViews.forEach((e) => e.terminate());
    d3.selectAll(this.parent).selectAll('svg').remove();
    this.terminateListeners();
  }

  public pause() {}

  public resume() {}

  public terminateListeners() {
    $(document).off('click', this.el).off('mousedown', this.el).off('mousemove', this.el).off('mouseup', this.el);
    $(this.el).find('#workflow-container').off('mousewheel');
  }

  private initializeListeners(): void {
    $(document)
      .on('click', this.el, (event: ClickEvent) => this.handleMouseClick(event))
      .on('mousedown', this.el, (event: MouseDownEvent) => this.handleMouseDown(event))
      .on('mousemove', this.el, (event: MouseMoveEvent) => this.handleMouseMove(event))
      .on('mouseup', this.el, (event: MouseUpEvent) => this.handleMouseUp(event));
    $(this.el)
      .find('#workflow-container')
      .on('mousewheel', (event: any) => this.handleMouseWheel(event));
  }

  public handleMouseClick(event: ClickEvent): void {
    const flowMouseEvent: NodeMouseEvent = this.buildEvent(event);
    this.pointerClick(flowMouseEvent);
  }

  public handleMouseDown(event: MouseDownEvent): void {
    const flowMouseEvent: NodeMouseEvent = this.buildEvent(event);
    this.pointerDown(flowMouseEvent);
  }

  public handleMouseMove(event: MouseMoveEvent): void {
    event.preventDefault();
    const flowMouseEvent: NodeMouseEvent = this.buildEvent(event);
    this.mouseMove(flowMouseEvent);
  }

  public handleMouseUp(event: MouseUpEvent): void {
    const flowMouseEvent: NodeMouseEvent = this.buildEvent(event);
    this.pointerUp(flowMouseEvent);
  }

  private handleMouseWheel(event: any): void {
    if (this.menuOpen) {
      event.preventDefault();
    } else {
      const flowMouseEvent: NodeMouseEvent = this.buildEvent(event);
      this.mouseWheel(flowMouseEvent);
    }
  }

  private mouseWheel = (event: NodeMouseEvent) => {
    this.transformY = -event.delta.y * 0.3 + this.transformY;
    node.attr('transform', 'translate(' + this.transformX + ',' + this.transformY + ') scale( ' + this.currentScale + ')');
  };

  private buildEvent(event: JQueryEvent): NodeMouseEvent {
    return {
      position: {
        x: event.pageX - 25,
        y: event.pageY - 25,
      },
      delta: {
        x: (event.originalEvent as any).deltaX,
        y: (event.originalEvent as any).deltaY,
      },
      page: { x: event.offsetX, y: event.offsetY },
      ctrlKey: event.ctrlKey || event.metaKey,
      viewType: event.target.attributes.type == undefined ? undefined : event.target.attributes.type.value,
      component:
        this.getResolutionsByDocument(event.target) ||
        this.getEventByDocument(event.target) ||
        this.getConditionByDocument(event.target) ||
        this.getActionByDocument(event.target) ||
        this.getTransitionByDocument(event.target),
    };
  }

  private getEventByDocument(document: any): View<Syntax<EventToken | ConditionToken | ActionToken>> | undefined {
    const svg = $(document).closest("[id^='event_']").get()[0];
    return svg == undefined ? undefined : this.getEventByView(svg.id);
  }

  private getEventByView(id: string): View<Syntax<EventToken>> | undefined {
    return this.eventViews.find((view: View<Syntax>) => view.getModel().id == id) as View<Syntax<EventToken>> | undefined;
  }

  private getResolutionsByDocument(document: any): View<Syntax> | undefined {
    const svg = $(document).closest("[id^='resolution_fraud_']").get()[0];
    return svg == undefined ? undefined : this.getResolutionsByView(svg.id);
  }

  private getResolutionsByView(id: string): View<Syntax> | undefined {
    return this.resolutionViews.find((view: View<Syntax<any>>) => view.getModel().id == id);
  }

  private getConditionByDocument(document: any): View<Syntax> | undefined {
    const svg = $(document).closest("[id^='condition_']").get()[0];
    return svg == undefined ? undefined : this.getConditionByView(svg.id);
  }

  private getConditionByView(id: string): View<Syntax> | undefined {
    return this.conditionViews.find((view: View<Syntax<any>>) => view.getModel().id == id);
  }

  private getActionByDocument(document: any): View<Syntax> | undefined {
    const svg = $(document).closest("[id^='action_']").get()[0];
    return svg == undefined ? undefined : this.getActionByView(svg.id);
  }

  private getActionByView(id: string): View<Syntax> | undefined {
    return this.actionViews.find((view: View<Syntax<any>>) => view.getModel().id == id);
  }

  private getResolutionByView(id: string): View<Syntax> | undefined {
    return this.resolutionViews.find((view: View<Syntax<any>>) => view.getModel().id == id);
  }

  private getTransitionByDocument(document: any): View<Syntax> | undefined {
    const svg = $(document).closest("[id^='transition_']").get()[0];
    return svg == undefined ? undefined : this.getTransitionByView(svg.id);
  }

  private getTransitionByView(id: string): View<Syntax> | undefined {
    return this.transitionViews.find((view: View<Syntax<any>>) => view.getNode().substring(1, view.getNode().length) == id);
  }

  private createTree = () => {
    this.treeNode.x0 = height / 2;
    this.treeNode.y0 = 0;
    this.treeNode.children?.forEach(this.collapse);
    this.update();
  };

  private collapse = (d: any) => {
    if (d.children) {
      d._children = d.children;
      d._children.forEach(this.collapse);
    }
  };

  private update = () => {
    let treeData = d3.tree().size([height, width])(this.treeNode);
    let nodes = treeData.descendants(),
      links = treeData.descendants().slice(1);
    nodes.forEach(function (d) {
      d.y = d.depth * 120 + 50;
      d.x += -120;
      if ((d.data as any).id === 'caseTrue' && d.children && d.children.length > 0) {
      }
    });

    this.containerNode.select('g').data(nodes, (node: any) => {
      let nodeView: AbstractNodeView | undefined;
      const syntax: Syntax<WorkflowToken> = node.data;
      switch (syntax.value.type) {
        case 'Start':
          nodeView = new StartNodeView({ workFlowView: this, model: node.data, el: `#${node.data.id}` });
          this.startView = nodeView;
          break;
        case 'Event':
          nodeView = new EventNodeView({ workFlowView: this, model: node.data, el: `#${node.data.id}` });
          this.eventViews.push(nodeView as View<Syntax<EventToken>>);
          break;
        case 'Priority' as any:
          nodeView = new PriorityNodeView({ workFlowView: this, model: node.data, el: `#${node.data.id}` });
          this.priorityNodeView = nodeView as View<Syntax<ConditionToken>>;
          break;
        case 'NotFraud':
          nodeView = new NotFraudNodeView({ workFlowView: this, model: node.data, el: `#${node.data.id}` });
          this.resolutionViews.push(nodeView as View<Syntax<NotFraudToken>>);
          break;
        case 'Escalated' as any:
          nodeView = new EscalatedNodeView({ workFlowView: this, model: node.data, el: `#${node.data.id}` });
          this.resolutionViews.push(nodeView as View<Syntax<EscalatedToken>>);
          break;
        case 'Condition':
          nodeView = new ConditionNodeView({ workFlowView: this, model: node.data, el: `#${node.data.id}` });
          this.conditionViews.push(nodeView as View<Syntax<ConditionToken>>);
          break;
        case 'Action':
          nodeView = new ActionNodeView({ workFlowView: this, model: node.data, el: `#${node.data.id}` });
          this.actionViews.push(nodeView as View<Syntax<ActionToken>>);
          break;
      }
      if (nodeView) {
        nodeView.initialize();
        nodeView.renderTreeNodePosition({ x: node.x - 25, y: node.y });
      }
      return null as any;
    });
    links.forEach((d: any) => {
      const transition = new TransitionView({ workFlowView: this, model: d.data, el: `#transition_${generateUUID()}` });
      transition.render(d);
      this.transitionViews.push(transition);
    });
  };

  public addEventItem(item: string): void {
    const workflow = this.getWorkflow();
    if (workflow.children && workflow.children.length > 0) {
      (workflow.children[0].value as any).name = item;
      const traverseAndUpdateStatus = (node: any) => {
        if (
          node.value.type === 'Event' ||
          node.value.type === 'Condition' ||
          node.value.type === 'NotFraud' ||
          node.value.type === 'Escalated' ||
          node.value.type === 'Priority' ||
          (node.parent?.value.type === 'NotFraud' && node.value.type !== 'Action') ||
          (node.parent?.value.type === 'Escalated' && node.value.type !== 'Action')
        ) {
          node.value.status = true;
        }
        if (node.children && node.children.length > 0) {
          for (const child of node.children) {
            traverseAndUpdateStatus(child);
          }
        }
      };
      const children = this.getWorkflow().children ?? [];
      for (const child of children) {
        traverseAndUpdateStatus(child);
      }
      this.reRender();
    }
  }

  public changeCaseStatus(): void {
    const workflow = this.getWorkflow();
    if (workflow && workflow.children && workflow.children.length > 0) {
      const eventNode = workflow.children[0];
      if (eventNode.children && eventNode.children.length > 0) {
        const conditionNode = eventNode.children[0];
        if (conditionNode.children && conditionNode.children.length > 0) {
          const caseFalseNode: any = conditionNode.children[1];
          if (caseFalseNode && caseFalseNode.value && caseFalseNode.value.type === 'Fraud') {
            this.extractChangeStatus(caseFalseNode);
            if (caseFalseNode.children && caseFalseNode.children.length > 0) {
              this.reRender();
            }
          }
        }
      }
    }
  }

  private extractChangeStatus(data: any) {
    if (data.children && data.children.length > 0) {
      data.value.status = !data.value.status;
      this.extractChangeStatus(data.children[0]);
    }
  }

  public addResolutionScore(caseType: ResolutionCaseType, score: number): void {
    const workflow = this.getWorkflow();
    const resolution = WorkflowNodeUtil.getResolution(workflow);
    if (resolution) {
      resolution.value.score = score;
      resolution.value.reason = this.getReasonStatus(score);
      this.model.score = score;
      this._onChangeFunction?.(this.model);
    }
  }

  private getReasonStatus = (score: number): CaseReason => {
    if (score == 0) {
      return CaseReason.NOT_FRAUD;
    } else if (score > 0 && score < 100) {
      return CaseReason.SUSPICIOUS;
    } else {
      return CaseReason.FRAUD;
    }
  };

  public addEventItems(eventItems: TransactionJsonModel[]): void {
    this.eventItems = eventItems;
  }

  public addActionItem(model: any, shadow: boolean, item: string, icon: string) {
    model.id = `action_${item}_${generateUUID()}`;
    model.value.status = true;
    model.value.name = item;
    model.value.icon = icon;
    if (model.children.length == 0) {
      this.addActionView(model, shadow);
      model.children.push({
        id: `action_${generateUUID()}`,
        value: {
          type: 'Action',
          name: `action_${generateUUID()}`,
          color: '#ADD8E6',
          icon: 'action',
          payload: {
            template: `action_${generateUUID()}`,
          },
          status: false,
        },
        parent: model,
        children: [],
      });
    }
    this.reRender();
  }

  public addActionItems(actions: ActionJsonModel[]) {
    this.actionItems = actions;
  }

  public deleteActionItem(id: string) {
    const actionViewIndex = this.actionViews.findIndex((view) => view.getModel().id === id);
    if (actionViewIndex !== -1) {
      const deletedActionView = this.actionViews.splice(actionViewIndex, 1)[0];
      deletedActionView.terminate();
      this.deleteActionItemAndReplace(this.getWorkflow(), id);
    }
  }

  public deleteActionItemAndReplace(model: any, id: string) {
    this.findAndDeleteAction(model, id);
    this.reRender();
  }

  private findAndDeleteAction(node: any, id: string): any {
    for (let i = 0; i < node.children.length; i++) {
      const child = node.children[i];
      if (child.id === id) {
        const deletedActionParent = node;
        node.children.splice(i, 1);
        deletedActionParent.children = deletedActionParent.children.concat(child.children);
        for (const grandChild of child.children) {
          grandChild.parent = deletedActionParent;
        }
        return deletedActionParent;
      } else {
        const deletedActionParent = this.findAndDeleteAction(child, id);
        if (deletedActionParent) {
          return deletedActionParent;
        }
      }
    }
    return null;
  }

  public addConditionView(model: Syntax<any>, shadow: boolean): ConditionNodeView {
    const conditionView: ConditionNodeView = new ConditionNodeView({
      workFlowView: this,
      model: model,
      el: `#condition_${model.value.name}`,
      shadow,
    });
    this.getWorkflow().children?.[0].children?.push(model);
    model.parent = this.getWorkflow().children?.[0];
    if (shadow) {
      this.currentShadow = model.id;
    } else {
      $(this.el).triggerHandler('rule:addcondition', model);
      this.currentShadow = undefined;
    }
    this.reRender();
    this.conditionViews.push(conditionView);
    conditionView.initialize();
    return conditionView;
  }

  public addActionView(model: Syntax<any>, shadow: boolean): ActionNodeView {
    const actionView: ActionNodeView = new ActionNodeView({
      workFlowView: this,
      model: model,
      el: `#action_${model.value.name}`,
      shadow,
    });
    if (shadow) {
      this.getWorkflow().children?.[0].children?.forEach((e: Syntax) => {
        e.children?.forEach((child: Syntax) => {
          this.loadAction(child, model, shadow);
        });
      });
      this.currentShadow = model.id;
    } else {
      this.currentShadow = undefined;
      $(this.el).triggerHandler('rule:addaction', model);
    }
    this.actionViews.push(actionView);
    actionView.initialize();
    return actionView;
  }

  private loadAction(e: Syntax, model: Syntax<any>, shadow: boolean): void {
    const id = e.id.split('_')[0];
    const parent = e.parent?.id.split('_')[0];
    if ((id == 'resolution' && parent == 'condition') || (id == 'action' && parent == 'resolution') || (id == 'action' && parent == 'action')) {
      this.addChildrenActionNode(e, model, shadow);
    }
  }

  private loadResolution(e: Syntax, model: Syntax<any>, shadow: boolean): void {
    const id = e.id.split('_')[0];
    const parent = e.parent?.id.split('_')[0];
    if (id == 'condition' && parent == 'event') {
      this.addChildrenResolutionNode(e, model, shadow);
    }
  }

  private addChildrenResolutionNode(e: Syntax, model: Syntax<any>, shadow: boolean): void {
    if (e.children && e.children.length == 0) {
      const resolutionModel = { ...model, id: `resolution_fraud_${generateUUID()}` };
      e.children.push(resolutionModel);
      resolutionModel.parent = e;
      resolutionModel.shadow = true;
      this.reRender();
    } else {
      e.children?.forEach((e) => this.loadResolution(e, model, shadow));
    }
  }

  private addChildrenActionNode(e: Syntax, model: Syntax<any>, shadow: boolean): void {
    if (e.children && e.children.length == 0) {
      const actionModel = { ...model, id: `action_${generateUUID()}` };
      e.children.push(actionModel);
      actionModel.parent = e;
      actionModel.shadow = true;
      this.reRender();
    } else {
      e.children?.forEach((e) => this.loadAction(e, model, shadow));
    }
  }

  protected highlightNodes(node: string | undefined): void {
    if (!this.viewState.current) return;
    switch (this.viewState.current.constructor) {
      case EventNodeView:
        this.eventViews.forEach((e: View<Syntax>) => e.socketHighlight(NodeType.EVENT));
        break;
      case ConditionNodeView:
        this.conditionViews.forEach((e: View<Syntax>) => e.socketHighlight(NodeType.CONDITION));
        break;
      case PriorityNodeView:
        this.priorityNodeView?.socketHighlight(NodeType.PRIORITY);
        break;
      case ActionNodeView:
        this.actionViews.filter((action) => action.getModel().id == node).forEach((e: View<Syntax>) => e.socketHighlight(NodeType.ACTION));
        break;
      case NotFraudNodeView:
        this.resolutionViews.forEach((e: View<Syntax>) => e.socketHighlight(NodeType.NOT_FRAUD));
        break;
    }
  }

  protected unHighlightNodes(node: string | undefined, event?: NodeMouseEvent): void {
    if (!this.viewState.current) return;
    switch (this.viewState.current.constructor) {
      case EventNodeView:
        this.eventViews.forEach((e: View<Syntax>) => e.socketUnHighlight(NodeType.EVENT));
        break;
      case ConditionNodeView:
        this.conditionViews.forEach((e: View<Syntax>) => e.socketUnHighlight(NodeType.CONDITION));
        break;
      case PriorityNodeView:
        this.priorityNodeView?.socketUnHighlight(NodeType.PRIORITY);
        break;
      case ActionNodeView:
        this.actionViews.filter((action) => action.getModel().id == node).forEach((e: View<Syntax>) => e.socketUnHighlight(NodeType.ACTION, event));
        break;
      case NotFraudNodeView:
        this.resolutionViews.forEach((e: View<Syntax>) => e.socketUnHighlight(NodeType.NOT_FRAUD));
        break;
    }
  }

  public setTreeHeight = (height: any) => {
    const workflowContainer = (d3.select('#workflow').node() as any)?.getBoundingClientRect();
    const containerHeight = workflowContainer.height - margin.bottom;
    d3.select('#workflow-container').attr('height', containerHeight);
  };

  public setMenuOpen(status: boolean): void {
    this.menuOpen = status;
  }

  public reRender = () => {
    this.eventViews = this.conditionViews = this.actionViews = this.transitionViews = [];
    this.priorityNodeView = undefined;
    this.eventViews.forEach((e) => e.terminate());
    this.conditionViews.forEach((e) => e.terminate());
    this.actionViews.forEach((e) => e.terminate());
    this.transitionViews.forEach((e) => e.terminate());
    this.resolutionViews.forEach((e) => e.terminate());
    this.containerNode.remove();
    this.containerNode = d3.select(this.parent);
    this.treeNode = d3.hierarchy(this.getWorkflow(), function (d: any) {
      return d.children;
    });
    this.render();
    this._onChangeFunction?.(this.model);
  };

  public setPriority(priority: number) {
    this.model.priority = priority;
    this._onChangeFunction?.(this.model);
  }

  protected abstract pointerDown(event: NodeMouseEvent): void;

  protected abstract pointerUp(event: NodeMouseEvent): void;

  protected abstract pointerClick(event: NodeMouseEvent): void;

  protected abstract mouseMove(event: NodeMouseEvent): void;
}

export default AbstractWorkFlowView;
